import { useEffect, useRef } from "react";
import { usePaywall, setGlobalPaywallOpener } from "@/context/PaywallContext";
import { useAuth } from "@/context/AuthContext";
import { getIntendedPlan, clearIntendedPlan } from "@/lib/intendedPlan";

// Bridges the React PaywallProvider state to the module-level singleton
// callable from non-React code (e.g. the axios 402 interceptor) AND
// consumes the pre-signup `intended_plan` choice: once the user has a
// family, we pop the contextual paywall pre-targeted to the picked tier.
// Free choice is honoured by skipping the paywall.
export function PaywallBridge() {
  const { openPaywall } = usePaywall();
  const { user, family } = useAuth();
  const consumedIntent = useRef(false);

  useEffect(() => {
    setGlobalPaywallOpener(openPaywall);
    return () => setGlobalPaywallOpener(null);
  }, [openPaywall]);

  useEffect(() => {
    if (consumedIntent.current) return;
    if (!user || !family) return; // wait until onboarding completes
    const intent = getIntendedPlan();
    if (!intent) return;
    consumedIntent.current = true;
    clearIntendedPlan();
    // "free" pick → no paywall, user just lands on /today.
    if (!intent.tier_key || intent.tier_key === "free") return;
    // Defer one frame so we don't fight the Today screen's mount transitions.
    setTimeout(() => {
      openPaywall({
        featureKey: "plan_select",
        requiredTier: intent.tier_key,
        message: `You picked Kinly ${intent.tier_key[0].toUpperCase()}${intent.tier_key.slice(1)} earlier. Start your first month free — cancel anytime.`,
      });
    }, 400);
  }, [user, family, openPaywall]);

  return null;
}
